import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Carousel, { Settings } from 'react-slick';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import useIsMobile from '../hooks/useIsMobile';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { mainAnnouncementsState } from '../states/mainAnnouncementsState';
import { modalState } from '../states/modalState';
import { formatDateString } from '../utils';
import Slider from './Slider';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

const MainAnnouncement = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const elRef = useRef<HTMLDivElement>(null);
  const [{ isOpen }, setModalState] = useRecoilState(modalState);
  const [slideIndex, setSlideIndex] = useState(0);
  const [mainAnnouncements, setMainAnnouncements] = useRecoilState(
    mainAnnouncementsState
  );

  const carouselSettings: Settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const Items = mainAnnouncements.map((announcement) => (
    <ItemsWrapper key={announcement.id}>
      <Top>
        <ItemTitle>{announcement.title}</ItemTitle>
        <ItemDate>{formatDateString(announcement.published_at)}</ItemDate>
      </Top>
      <Main>{announcement.contents}</Main>
    </ItemsWrapper>
  ));

  useEffect(() => {
    if (!mainAnnouncements?.length || isMobile) {
      return;
    }

    setModalState({
      isOpen: true,
      component: (
        <ModalContainer>
          <Container ref={elRef}>
            {mainAnnouncements.length > 1 ? (
              <CarouselContainer className="slider-container">
                <Carousel {...carouselSettings}></Carousel>
              </CarouselContainer>
            ) : (
              Items
            )}

            <ButtonsWrapper>
              <Button onClick={closeModal}>Close</Button>
              <Button
                onClick={onClickViewDetail}
                style={{ backgroundColor: '#1d90f5', borderColor: '#1d90f5' }}
              >
                View Details
              </Button>
            </ButtonsWrapper>
          </Container>
        </ModalContainer>
      ),
    });
  }, [mainAnnouncements]);

  const closeSlider = () => setMainAnnouncements([]);
  const closeModal = () => setModalState({ isOpen: false });
  const closeAnnouncement = isMobile ? closeSlider : closeModal;

  useOutsideClick({ ref: elRef, handler: closeAnnouncement });

  const onClickViewDetail = () => {
    const id = mainAnnouncements[slideIndex].id;

    closeAnnouncement();
    navigate(`/announcement/${id}`);
  };

  if (!mainAnnouncements?.length || !isMobile) {
    return;
  }

  return (
    <Slider defaultHeight={400}>
      <Container ref={elRef}>
        {mainAnnouncements.length > 1 ? (
          <CarouselContainer className="slider-container">
            <Carousel {...carouselSettings}></Carousel>
          </CarouselContainer>
        ) : (
          Items
        )}
        <ButtonsWrapper>
          <Button onClick={closeSlider}>Close</Button>
          <Button
            onClick={onClickViewDetail}
            style={{ backgroundColor: '#1d90f5', borderColor: '#1d90f5' }}
          >
            View Details
          </Button>
        </ButtonsWrapper>
      </Container>
    </Slider>
  );
};

export default MainAnnouncement;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const CarouselContainer = styled.div`
  flex: 1;

  & .slick-dots li button:before {
    color: white;
  }
`;

const CarouselButtonsWrapper = styled.div`
  height: 20px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  height: 36px;
`;

const Button = styled.button`
  color: white;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  background-color: transparent;
  border: 1px solid white;
`;

const ItemsWrapper = styled.ul`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 0;
  overflow: hidden;
  height: 100%;
`;

const ItemTitle = styled.div`
  flex: 1;
  font-size: 16px;
  line-height: 19px;
  font-weight: 500;
  color: white;
  display: flex;
  align-items: center;
`;

const ItemDate = styled.div`
  font-size: 14px;
  color: #a1a6bc;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;

  gap: 6px;
`;

const Main = styled.div`
  padding: 8px;
  border: 1px solid #3b3f51;
  border-radius: 8px;
  color: #a1a6bc;
  font-size: 14px;
  line-height: 22px;
  background-color: #323643;
  white-space: pre-line;
`;

const ModalContainer = styled.div`
  width: 640px;
  height: 500px;
  background-color: #272a37;
  padding: 30px;
  border-radius: 18px;
`;
